/**
 * Employees Service
 * 
 * Handles all database operations for employee management
 * 
 * @module services/supabase/employees
 */

import { supabase, SUPABASE_URL } from './client';
import {
  Employee,
  EmployeeWithAssignments,
  CreateEmployeeInput,
  UpdateEmployeeInput,
  UpdateAssignmentsInput,
} from '@/features/employees/types';

/**
 * Unified property type for employee assignment
 * Can come from either application_objects or home_owner_objects
 */
export interface AvailableProperty {
  id: string;
  name: string;
  type: string;
  location?: string | null;
  address?: string | null;
  source: 'application_objects' | 'home_owner_objects'; // Indicates which table the property comes from
}

export const employeesService = {
  /**
   * Get all employees with their object assignments
   * RLS Policy: Users see employees for service providers they belong to
   * For employees: Shows all team members (employees with same service_provider_id)
   * For service providers: Shows all their employees
   * 
   * @returns Promise<EmployeeWithAssignments[]>
   * @throws Error if not authenticated or query fails
   */
  getAll: async (): Promise<EmployeeWithAssignments[]> => {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get current user's role
    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    const userRole = profile?.role;

    // Step 1: Fetch employees
    const { data: employees, error: employeesError } = await supabase
      .from('employees')
      .select('*')
      .order('last_name', { ascending: true })
      .order('first_name', { ascending: true });

    if (employeesError) throw employeesError;

    // Step 1.5: If user is an employee, also fetch their service provider
    let serviceProviderProfile: { id: string; email: string; full_name: string | null } | null = null;
    let serviceProviderId: string | null = null;

    if (userRole === 'employee') {
      // Get the service provider ID from the current user's employee record
      const { data: currentUserEmployeeRecord } = await supabase
        .from('employees')
        .select('service_provider_id')
        .eq('id', user.id)
        .maybeSingle();

      if (currentUserEmployeeRecord) {
        serviceProviderId = currentUserEmployeeRecord.service_provider_id;
        
        // Fetch service provider profile
        const { data: spProfile } = await supabase
          .from('profiles')
          .select('id, email, full_name')
          .eq('id', serviceProviderId)
          .single();

        if (spProfile) {
          serviceProviderProfile = spProfile;
        }
      }
    }

    // Helper function to split full_name into first and last name
    const splitName = (fullName: string | null, email: string) => {
      if (fullName) {
        const parts = fullName.trim().split(/\s+/);
        if (parts.length > 1) {
          return {
            first_name: parts[0],
            last_name: parts.slice(1).join(' '),
          };
        }
        return {
          first_name: parts[0] || email.split('@')[0],
          last_name: '',
        };
      }
      const emailName = email.split('@')[0];
      return {
        first_name: emailName,
        last_name: '',
      };
    };

    if (!employees || employees.length === 0) {
      // If no employees but we have a service provider, return just the service provider
      if (serviceProviderProfile) {
        const { first_name, last_name } = splitName(serviceProviderProfile.full_name, serviceProviderProfile.email);
        return [{
          id: serviceProviderProfile.id,
          first_name,
          last_name,
          email: serviceProviderProfile.email,
          phone: null,
          position: 'Service Provider',
          is_active: true,
          service_provider_id: serviceProviderProfile.id,
          created_at: '',
          updated_at: '',
          assignments: [],
          isServiceProvider: true,
        }];
      }
      return [];
    }

    // Step 1.6: Sort employees so current user appears first (if they are an employee)
    const currentUserEmployee = employees.find(e => e.id === user.id);
    const sortedEmployees = currentUserEmployee
      ? [currentUserEmployee, ...employees.filter(e => e.id !== user.id)]
      : employees;

    // Step 2: Fetch assignments (without FK join since we support both object types)
    const employeeIds = employees.map(e => e.id);
    const { data: assignments, error: assignmentsError } = await supabase
      .from('employee_object_assignments')
      .select('employee_id, object_id')
      .in('employee_id', employeeIds);

    if (assignmentsError) throw assignmentsError;

    // Step 3: Fetch object details from both tables
    const objectIds = [...new Set((assignments || []).map((a: any) => a.object_id))];
    
    const objectDetailsMap = new Map<string, { id: string; name: string }>();

    if (objectIds.length > 0) {
      // Fetch from application_objects
      const { data: appObjects, error: appObjectsError } = await supabase
        .from('application_objects')
        .select('id, name')
        .in('id', objectIds);

      if (appObjectsError) {
        console.warn('Error fetching application_objects:', appObjectsError);
      } else if (appObjects) {
        appObjects.forEach((obj: any) => {
          objectDetailsMap.set(obj.id, { id: obj.id, name: obj.name });
        });
      }

      // Fetch from home_owner_objects
      const { data: homeObjects, error: homeObjectsError } = await supabase
        .from('home_owner_objects')
        .select('id, name')
        .in('id', objectIds);

      if (homeObjectsError) {
        console.warn('Error fetching home_owner_objects:', homeObjectsError);
      } else if (homeObjects) {
        homeObjects.forEach((obj: any) => {
          // Only add if not already in map (avoid duplicates)
          if (!objectDetailsMap.has(obj.id)) {
            objectDetailsMap.set(obj.id, { id: obj.id, name: obj.name });
          }
        });
      }
    }

    // Step 4: Group assignments by employee
    const assignmentsByEmployee = new Map<string, { id: string; name: string }[]>();
    
    assignments?.forEach((assignment: any) => {
      const objectDetails = objectDetailsMap.get(assignment.object_id);
      if (objectDetails) {
      if (!assignmentsByEmployee.has(assignment.employee_id)) {
        assignmentsByEmployee.set(assignment.employee_id, []);
        }
        assignmentsByEmployee.get(assignment.employee_id)!.push(objectDetails);
      }
    });

    // Step 5: Merge assignments with employees (using sorted list)
    const employeeList: EmployeeWithAssignments[] = sortedEmployees.map(employee => ({
      ...employee,
      assignments: assignmentsByEmployee.get(employee.id) || [],
      isServiceProvider: false,
    }));

    // Step 5: If user is an employee, add service provider at the beginning (after current user)
    if (userRole === 'employee' && serviceProviderProfile && serviceProviderId) {
      // Check if service provider is already in the list (shouldn't be, but just in case)
      const spAlreadyInList = employeeList.some(e => e.id === serviceProviderId);
      
      if (!spAlreadyInList) {
        const { first_name, last_name } = splitName(serviceProviderProfile.full_name, serviceProviderProfile.email);
        const serviceProviderEntry: EmployeeWithAssignments = {
          id: serviceProviderProfile.id,
          first_name,
          last_name,
          email: serviceProviderProfile.email,
          phone: null,
          position: 'Service Provider',
          is_active: true,
          service_provider_id: serviceProviderProfile.id,
          created_at: '',
          updated_at: '',
          assignments: [],
          isServiceProvider: true,
        };

        // Insert service provider after current user (if current user exists) or at the beginning
        if (currentUserEmployee) {
          const currentUserIndex = employeeList.findIndex(e => e.id === user.id);
          employeeList.splice(currentUserIndex + 1, 0, serviceProviderEntry);
        } else {
          employeeList.unshift(serviceProviderEntry);
        }
      }
    }

    return employeeList;
  },

  /**
   * Get a single employee by ID
   * 
   * @param id - Employee UUID
   * @returns Promise<Employee | null>
   */
  getById: async (id: string): Promise<Employee | null> => {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Create a new employee
   * 
   * @param input - Employee data
   * @returns Promise<Employee>
   * @throws Error if validation fails or user lacks permission
   */
  create: async (input: CreateEmployeeInput): Promise<Employee> => {
    const { data, error } = await supabase
      .from('employees')
      .insert(input)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update an existing employee
   * 
   * @param id - Employee UUID
   * @param input - Updated employee data
   * @returns Promise<Employee>
   * @throws Error if employee not found or user lacks permission
   */
  update: async (id: string, input: UpdateEmployeeInput): Promise<Employee> => {
    const { data, error } = await supabase
      .from('employees')
      .update(input)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete an employee (legacy method - only deletes from employees table)
   * @deprecated Use removeEmployee instead for complete deletion
   * 
   * @param id - Employee UUID
   * @returns Promise<void>
   * @throws Error if employee not found or user lacks permission
   */
  delete: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('employees')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Remove an employee (completely deletes from all tables including auth.users)
   * Deletes from: employee_object_assignments, employee_invitations, employees, profiles, auth.users
   * 
   * @param employeeId - Employee UUID
   * @returns Promise<void>
   * @throws Error if employee not found or user lacks permission
   */
  removeEmployee: async (employeeId: string): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('User must be authenticated');
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/remove-employee`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ employeeId }),
    });

    const result = await response.json();

    if (!response.ok || result.error) {
      throw new Error(result.error || 'Failed to remove employee');
    }
  },

  /**
   * Update employee object assignments
   * Replaces all existing assignments with new ones
   * 
   * @param input - Employee ID and new object IDs
   * @returns Promise<void>
   * @throws Error if employee not found or objects don't exist
   */
  updateAssignments: async (input: UpdateAssignmentsInput): Promise<void> => {
    const { employee_id, object_ids } = input;

    // Remove duplicates from object_ids to prevent unique constraint violations
    const uniqueObjectIds = [...new Set(object_ids)];
    console.log('[updateAssignments] Processing assignments for employee:', employee_id, 'Unique object IDs:', uniqueObjectIds);

    // Step 1: Delete existing assignments for this employee
    const { error: deleteError } = await supabase
      .from('employee_object_assignments')
      .delete()
      .eq('employee_id', employee_id);

    if (deleteError) {
      console.error('[updateAssignments] Error deleting existing assignments:', deleteError);
      throw deleteError;
    }

    // Step 2: Insert new assignments (if any)
    // Note: We delete first, then insert, so duplicates shouldn't occur
    // But we still check for existing assignments as a safety measure
    if (uniqueObjectIds.length > 0) {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Check for any remaining assignments (safety check after delete)
      const { data: existingAssignments } = await supabase
        .from('employee_object_assignments')
        .select('object_id')
        .eq('employee_id', employee_id)
        .in('object_id', uniqueObjectIds);

      const existingObjectIds = new Set(existingAssignments?.map(a => a.object_id) || []);
      const assignmentsToInsert = uniqueObjectIds
        .filter(object_id => !existingObjectIds.has(object_id))
        .map(object_id => ({
        employee_id,
        object_id,
          assigned_by: user?.id || null,
      }));

      if (assignmentsToInsert.length > 0) {
        console.log('[updateAssignments] Inserting', assignmentsToInsert.length, 'assignments');
      const { error: insertError } = await supabase
        .from('employee_object_assignments')
          .insert(assignmentsToInsert);

        if (insertError) {
          console.error('[updateAssignments] Error inserting assignments:', insertError);
          throw insertError;
        }
        console.log('[updateAssignments] Successfully inserted assignments');
      } else if (existingObjectIds.size > 0) {
        console.log('[updateAssignments] All assignments already exist, skipping insert');
      }
    } else {
      console.log('[updateAssignments] No assignments to insert');
    }
  },

  /**
   * Get all employees assigned to a specific object
   * 
   * @param objectId - Object UUID
   * @returns Promise<Employee[]>
   */
  getByObject: async (objectId: string): Promise<Employee[]> => {
    const { data, error } = await supabase
      .from('employee_object_assignments')
      .select(`
        employee:employees (*)
      `)
      .eq('object_id', objectId);

    if (error) throw error;
    return data?.map((item: any) => item.employee) || [];
  },

  /**
   * Get all available properties for the current service provider
   * Combines properties from:
   * 1. application_objects (service provider owned properties)
   * 2. home_owner_objects (properties assigned via serviceproviderpermissions)
   * 
   * @returns Promise<AvailableProperty[]>
   */
  getAvailablePropertiesForAssignment: async (): Promise<AvailableProperty[]> => {
    // Get current service provider ID
    const { data: spIdData, error: spIdError } = await supabase.rpc('get_current_service_provider_id');

    if (spIdError) {
      console.error('Error fetching service provider ID:', spIdError);
      throw new Error(`Failed to fetch service provider ID: ${spIdError.message}`);
    }

    const serviceProviderId = spIdData;

    if (!serviceProviderId) {
      // User is not a service provider, return empty array
      return [];
    }

    // Get current user's profile ID (service provider's profile)
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const availableProperties: AvailableProperty[] = [];

    // 1. Get properties from application_objects (service provider owned)
    const { data: ownedProperties, error: ownedError } = await supabase
      .from('application_objects')
      .select('id, name, type, location, address')
      .eq('owner_id', user.id)
      .order('name', { ascending: true });

    if (ownedError) {
      console.error('Error fetching owned properties:', ownedError);
      throw new Error(`Failed to fetch owned properties: ${ownedError.message}`);
    }

    if (ownedProperties) {
      ownedProperties.forEach((prop: any) => {
        availableProperties.push({
          id: prop.id,
          name: prop.name,
          type: prop.type,
          location: prop.location,
          address: prop.address,
          source: 'application_objects',
        });
      });
    }

    // 2. Get properties from home_owner_objects (assigned via service_provider_property_assignments)
    // NOTE: service_provider_property_assignments is the source of truth for SP→property assignments.
    // The legacy serviceproviderpermissions table is kept for permission flags but should not be used
    // to enumerate which properties an SP can access.
    // Cast through `any` because the generated Supabase types in `./types.ts` are stale and
    // do not yet include `service_provider_property_assignments`. The newer `database.types.ts`
    // does include it. Same pattern is used in serviceProviders.service.ts.
    const { data: assignments, error: assignmentsError } = await (supabase as any)
      .from('service_provider_property_assignments')
      .select('property_id')
      .eq('service_provider_id', serviceProviderId);

    if (assignmentsError) {
      console.error('Error fetching service provider property assignments:', assignmentsError);
      throw new Error(`Failed to fetch property assignments: ${assignmentsError.message}`);
    }

    if (assignments && assignments.length > 0) {
      // Extract property IDs
      const propertyIds = assignments.map((a: any) => a.property_id).filter((id: any) => id !== null);

      if (propertyIds.length > 0) {
        // Fetch the actual properties from home_owner_objects
        const { data: assignedProperties, error: assignedError } = await supabase
          .from('home_owner_objects')
          .select('id, name, type, location, address')
          .in('id', propertyIds)
          .order('name', { ascending: true });

        if (assignedError) {
          console.error('Error fetching assigned properties:', assignedError);
          throw new Error(`Failed to fetch assigned properties: ${assignedError.message}`);
        }

        if (assignedProperties) {
          assignedProperties.forEach((prop: any) => {
            // Avoid duplicates (in case a property exists in both tables)
            if (!availableProperties.find(p => p.id === prop.id)) {
              availableProperties.push({
                id: prop.id,
                name: prop.name,
                type: prop.type,
                location: prop.location,
                address: prop.address,
                source: 'home_owner_objects',
              });
            }
          });
        }
      }
    }

    // Sort all properties by name
    return availableProperties.sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * Get assigned properties for the current employee
   * Returns properties from both application_objects and home_owner_objects
   * that have been assigned to the employee via employee_object_assignments
   * 
   * @returns Promise<AvailableProperty[]>
   */
  getAssignedPropertiesForEmployee: async (): Promise<AvailableProperty[]> => {
    console.log('[getAssignedPropertiesForEmployee] Starting...');
    
    // Get current user ID (employee)
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('[getAssignedPropertiesForEmployee] User not authenticated');
      throw new Error('User must be authenticated');
    }

    console.log('[getAssignedPropertiesForEmployee] User ID (profile ID):', user.id);

    // For employees, employees.id should equal profiles.id (auth.uid())
    // First verify the user is an employee and get their employee record
    const { data: employeeRecord, error: employeeError } = await supabase
      .from('employees')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (employeeError) {
      console.error('[getAssignedPropertiesForEmployee] Error checking employee record:', employeeError);
      throw new Error(`Failed to verify employee record: ${employeeError.message}`);
    }

    if (!employeeRecord) {
      console.log('[getAssignedPropertiesForEmployee] User is not an employee, returning empty array');
      return [];
    }

    const employeeId = employeeRecord.id;
    console.log('[getAssignedPropertiesForEmployee] Employee ID:', employeeId);

    // Get all assignments for this employee
    const { data: assignments, error: assignmentsError } = await supabase
      .from('employee_object_assignments')
      .select('object_id')
      .eq('employee_id', employeeId);

    if (assignmentsError) {
      console.error('[getAssignedPropertiesForEmployee] Error fetching employee assignments:', assignmentsError);
      throw new Error(`Failed to fetch employee assignments: ${assignmentsError.message}`);
    }

    console.log('[getAssignedPropertiesForEmployee] Assignments found:', assignments?.length || 0, assignments);

    if (!assignments || assignments.length === 0) {
      console.log('[getAssignedPropertiesForEmployee] No assignments found, returning empty array');
      return [];
    }

    // Extract unique object IDs
    const objectIds = [...new Set(assignments.map((a: any) => a.object_id))];
    console.log('[getAssignedPropertiesForEmployee] Unique object IDs:', objectIds);

    const assignedProperties: AvailableProperty[] = [];

    if (objectIds.length > 0) {
      // Fetch from application_objects
      console.log('[getAssignedPropertiesForEmployee] Querying application_objects for IDs:', objectIds);
      const { data: appObjects, error: appObjectsError } = await supabase
        .from('application_objects')
        .select('id, name, type, location, address')
        .in('id', objectIds);

      if (appObjectsError) {
        console.error('[getAssignedPropertiesForEmployee] Error fetching application_objects:', appObjectsError);
        console.error('[getAssignedPropertiesForEmployee] Error details:', JSON.stringify(appObjectsError, null, 2));
      } else {
        console.log('[getAssignedPropertiesForEmployee] Application objects found:', appObjects?.length || 0, appObjects);
        if (appObjects && appObjects.length > 0) {
          appObjects.forEach((prop: any) => {
            assignedProperties.push({
              id: prop.id,
              name: prop.name,
              type: prop.type,
              location: prop.location,
              address: prop.address,
              source: 'application_objects',
            });
          });
        }
      }

      // Fetch from home_owner_objects
      console.log('[getAssignedPropertiesForEmployee] Querying home_owner_objects for IDs:', objectIds);
      const { data: homeObjects, error: homeObjectsError } = await supabase
        .from('home_owner_objects')
        .select('id, name, type, location, address')
        .in('id', objectIds);

      if (homeObjectsError) {
        console.error('[getAssignedPropertiesForEmployee] Error fetching home_owner_objects:', homeObjectsError);
        console.error('[getAssignedPropertiesForEmployee] Error details:', JSON.stringify(homeObjectsError, null, 2));
      } else {
        console.log('[getAssignedPropertiesForEmployee] Homeowner objects found:', homeObjects?.length || 0, homeObjects);
        if (homeObjects && homeObjects.length > 0) {
          homeObjects.forEach((prop: any) => {
            // Only add if not already in list (avoid duplicates)
            if (!assignedProperties.find(p => p.id === prop.id)) {
              assignedProperties.push({
                id: prop.id,
                name: prop.name,
                type: prop.type,
                location: prop.location,
                address: prop.address,
                source: 'home_owner_objects',
              });
            }
          });
        }
      }
    }

    console.log('[getAssignedPropertiesForEmployee] Total properties to return:', assignedProperties.length);
    
    // Sort all properties by name
    const sorted = assignedProperties.sort((a, b) => a.name.localeCompare(b.name));
    console.log('[getAssignedPropertiesForEmployee] Returning sorted properties:', sorted);
    return sorted;
  },
};

